import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getPosts, deletePost, getAllUsers, updateUser, getAdminAnalytics, getCustomOrders, updateCustomOrderStatus, banUser, unbanUser, adminResetUserPassword } from '../services/api';
import { Users, FileText, Crown, Download, BarChart3, TrendingUp, Heart, MessageSquare, Settings, Shield, FolderOpen, Plus, Edit, Trash2, Loader2, CreditCard, Save, X, Activity, Hash, Lock, Camera, Ban, ShieldCheck, Award, Eye, ShoppingBag } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import GamificationAdminPanel from '../components/GamificationAdminPanel';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import StripeAuditPanel from '../components/StripeAuditPanel';
import EmailBlastPanel from '../components/EmailBlastPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Tier colors for badges
const TIER_COLORS = {
  free: '#6B7280',
  bronze: '#CD7F32',
  silver: '#C0C0C0',
  gold: '#FFD700'
};

const TIER_PRICES = {
  free: 0,
  bronze: 9.99,
  silver: 19.99,
  gold: 49.99
};

const AdminPage = () => {
  const { isOwner, currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  // Investigator roster filters
  const [tierFilter, setTierFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('joined_desc');
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  
  // Chat rooms state
  const [chatRooms, setChatRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [savingRoom, setSavingRoom] = useState(false);
  const [roomForm, setRoomForm] = useState({ name: '', description: '', minTier: 'free', allowImages: false });
  
  // User editing state
  const [editingUser, setEditingUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [userEdits, setUserEdits] = useState({
    role: '',
    subscriptionTier: '',
    displayName: '',
    reviewerMode: false,
    reviewerExpiresAt: '',
    dmContact: false
  });
  const [newUserPassword, setNewUserPassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);

  const toDateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

  const handleSetUserPassword = async () => {
    if (!editingUser || newUserPassword.length < 8) {
      toast({ title: 'Password too short', description: 'Use at least 8 characters', variant: 'destructive' });
      return;
    }
    try {
      setSettingPassword(true);
      await adminResetUserPassword(editingUser.id, newUserPassword);
      setNewUserPassword('');
      toast({ title: 'Password updated', description: `New password set for ${editingUser.displayName}` });
    } catch (error) {
      toast({ title: 'Failed', description: error.response?.data?.detail || 'Could not set password', variant: 'destructive' });
    } finally {
      setSettingPassword(false);
    }
  };

  // Custom orders state
  const [customOrders, setCustomOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Ban state
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [banningUser, setBanningUser] = useState(null);
  const [banType, setBanType] = useState('suspended');
  const [banReason, setBanReason] = useState('');
  const [banSubmitting, setBanSubmitting] = useState(false);

  const loadChatRooms = useCallback(async () => {
    try {
      setLoadingRooms(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_URL}/chat/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatRooms(response.data);
    } catch (error) {
    } finally {
      setLoadingRooms(false);
    }
  }, []);

  const loadCustomOrders = useCallback(async () => {
    try {
      setLoadingOrders(true);
      const data = await getCustomOrders();
      setCustomOrders(data);
    } catch (error) {
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      setLoadingAnalytics(true);
      const data = await getAdminAnalytics();
      setAnalytics(data);
    } catch (error) {
    } finally {
      setLoadingAnalytics(false);
    }
  }, []);

  const loadPosts = useCallback(async () => {
    try {
      setLoadingPosts(true);
      const data = await getPosts();
      setPosts(data);
    } catch (error) {
    } finally {
      setLoadingPosts(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const data = await getAllUsers();
      setUsers(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load investigators',
        variant: 'destructive'
      });
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (isOwner) {
      loadPosts();
      loadUsers();
      loadAnalytics();
      loadChatRooms();
      loadCustomOrders();
    }
  }, [isOwner, loadPosts, loadUsers, loadAnalytics, loadChatRooms, loadCustomOrders]);

  const handleOrderStatus = async (orderId, newStatus) => {
    try {
      await updateCustomOrderStatus(orderId, newStatus);
      setCustomOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
      toast({ title: 'Status updated' });
    } catch {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const handleCreateRoom = () => {
    setEditingRoom(null);
    setRoomForm({ name: '', description: '', minTier: 'free', allowImages: false });
    setRoomDialogOpen(true);
  };

  const handleEditRoom = (room) => {
    setEditingRoom(room);
    setRoomForm({ name: room.name, description: room.description || '', minTier: room.minTier, allowImages: !!room.allowImages });
    setRoomDialogOpen(true);
  };

  const handleSaveRoom = async () => {
    if (!roomForm.name.trim()) {
      toast({ title: 'Error', description: 'Room name is required', variant: 'destructive' });
      return;
    }
    
    setSavingRoom(true);
    try {
      const token = localStorage.getItem('authToken');
      if (editingRoom) {
        await axios.put(`${API_URL}/chat/rooms/${editingRoom.id}`, null, { 
          headers: { Authorization: `Bearer ${token}` },
          params: roomForm 
        });
        toast({ title: 'Success', description: 'Chat room updated' });
      } else {
        await axios.post(`${API_URL}/chat/rooms`, null, { 
          headers: { Authorization: `Bearer ${token}` },
          params: roomForm 
        });
        toast({ title: 'Success', description: 'Chat room created' });
      }
      setRoomDialogOpen(false);
      loadChatRooms();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save room', variant: 'destructive' });
    } finally {
      setSavingRoom(false);
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Delete this chat room and all its messages?')) return;
    
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API_URL}/chat/rooms/${roomId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Chat room deleted' });
      loadChatRooms();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete room', variant: 'destructive' });
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserEdits({
      role: user.role || 'member',
      subscriptionTier: user.subscriptionTier || 'free',
      displayName: user.displayName || '',
      reviewerMode: !!user.reviewerMode,
      reviewerExpiresAt: toDateInput(user.reviewerExpiresAt),
      dmContact: !!user.dmContact
    });
    setNewUserPassword('');
    setEditDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    // Prevent changing own role
    if (editingUser.id === currentUser?.id && userEdits.role !== editingUser.role) {
      toast({
        title: 'Cannot change own role',
        description: 'You cannot change your own role',
        variant: 'destructive'
      });
      return;
    }

    // Only send fields that ACTUALLY changed — never silently overwrite a
    // member's subscriptionTier (which would revoke paid access while Stripe
    // keeps billing them).
    const payload = {};
    if (userEdits.displayName !== (editingUser.displayName || '')) payload.displayName = userEdits.displayName;
    if (userEdits.role !== (editingUser.role || 'member')) payload.role = userEdits.role;
    const tierChanged = userEdits.subscriptionTier !== (editingUser.subscriptionTier || 'free');
    if (tierChanged) {
      const ok = window.confirm(
        `Change ${editingUser.displayName || 'this member'}'s tier from "${editingUser.subscriptionTier || 'free'}" to "${userEdits.subscriptionTier}"?\n\nThis changes their in-app access only — it does NOT change their Stripe/PayPal billing.`
      );
      if (!ok) return;
      payload.subscriptionTier = userEdits.subscriptionTier;
    }
    if (userEdits.reviewerMode !== !!editingUser.reviewerMode) payload.reviewerMode = userEdits.reviewerMode;
    if (userEdits.dmContact !== !!editingUser.dmContact) payload.dmContact = userEdits.dmContact;
    if (userEdits.reviewerMode && userEdits.reviewerExpiresAt !== toDateInput(editingUser.reviewerExpiresAt)) {
      payload.reviewerExpiresAt = userEdits.reviewerExpiresAt ? new Date(`${userEdits.reviewerExpiresAt}T23:59:59Z`).toISOString() : null;
    }

    if (Object.keys(payload).length === 0) {
      setEditDialogOpen(false);
      setEditingUser(null);
      toast({ title: 'No changes', description: 'Nothing to update' });
      return;
    }

    try {
      setSavingUser(true);
      await updateUser(editingUser.id, payload);
      
      // Update local state
      setUsers(prev => prev.map(u => 
        u.id === editingUser.id ? { ...u, ...payload } : u
      ));
      
      setEditDialogOpen(false);
      setEditingUser(null);
      
      toast({
        title: 'User updated',
        description: `${userEdits.displayName}'s profile has been updated`
      });
    } catch (error) {
      toast({
        title: 'Update failed',
        description: error.response?.data?.detail || 'Failed to update user',
        variant: 'destructive'
      });
    } finally {
      setSavingUser(false);
    }
  };

  const openBanDialog = (user) => {
    setBanningUser(user);
    setBanType('suspended');
    setBanReason('');
    setBanDialogOpen(true);
  };

  const handleBanUser = async () => {
    if (!banningUser) return;
    try {
      setBanSubmitting(true);
      const result = await banUser(banningUser.id, banType, banReason);
      setUsers(prev => prev.map(u =>
        u.id === banningUser.id
          ? { ...u, banned: true, banType, banReason: banReason.trim() }
          : u
      ));
      setBanDialogOpen(false);
      setBanningUser(null);
      toast({
        title: banType === 'permanent' ? 'User permanently banned' : 'User suspended',
        description: result.message
      });
    } catch (error) {
      toast({
        title: 'Ban failed',
        description: error.response?.data?.detail || 'Failed to ban user',
        variant: 'destructive'
      });
    } finally {
      setBanSubmitting(false);
    }
  };

  const handleUnbanUser = async (user) => {
    try {
      const result = await unbanUser(user.id);
      setUsers(prev => prev.map(u =>
        u.id === user.id
          ? { ...u, banned: false, banType: null, banReason: null }
          : u
      ));
      toast({ title: 'User reinstated', description: result.message });
    } catch (error) {
      toast({
        title: 'Unban failed',
        description: error.response?.data?.detail || 'Failed to unban user',
        variant: 'destructive'
      });
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this case file?')) return;
    
    try {
      await deletePost(postId);
      toast({
        title: 'Deleted',
        description: 'Case file deleted successfully'
      });
      loadPosts();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete case file',
        variant: 'destructive'
      });
    }
  };

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto text-red-600 mb-4" />
          <h1 className="text-2xl font-headline text-amber-500 mb-4 tracking-wider">ACCESS DENIED</h1>
          <p className="text-gray-400 font-typewriter mb-6">This area is restricted to the platform owner.</p>
          <Button onClick={() => navigate('/feed')} className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider">
            Back to Evidence Files
          </Button>
        </div>
      </div>
    );
  }

  // Use analytics data if available, fallback to local calculation
  const stats = analytics ? {
    totalUsers: analytics.users.total,
    activeSubscribers: analytics.users.paidSubscribers,
    newUsersThisWeek: analytics.users.newThisWeek,
    pushEnabled: analytics.users.pushEnabled || 0,
    pushEnabledThisWeek: analytics.users.pushEnabledThisWeek || 0,
    freeUsers: analytics.users.tierBreakdown.free || 0,
    totalPosts: analytics.content.totalPosts,
    publishedPosts: analytics.content.publishedPosts,
    postsThisMonth: analytics.content.postsThisMonth,
    totalLikes: analytics.content.totalLikes,
    totalComments: analytics.content.totalComments,
    totalRevenue: analytics.revenue.monthlyRecurring,
    totalRevenueCAD: analytics.revenue.monthlyRecurringCAD,
    fxRate: analytics.revenue.usdToCadRate,
    activePaidCount: analytics.revenue.activePaidCount,
    cancelingMonthly: analytics.revenue.cancelingMonthly,
    pastDueMonthly: analytics.revenue.pastDueMonthly,
    storeTotal: analytics.revenue.storeTotal,
    storeTotalCAD: analytics.revenue.storeTotalCAD,
    storeCount: analytics.revenue.storeCount,
    storeLast30: analytics.revenue.storeLast30,
    storeLast30Count: analytics.revenue.storeLast30Count,
    engagementRate: analytics.engagement.engagementRate,
    activeUsers: analytics.engagement.activeUsers,
    chatMessages: analytics.engagement.chatMessages,
    tierBreakdown: analytics.users.tierBreakdown
  } : {
    totalUsers: users.length,
    activeSubscribers: users.filter(u => u.subscriptionTier !== 'free').length,
    newUsersThisWeek: 0,
    pushEnabled: 0,
    pushEnabledThisWeek: 0,
    freeUsers: users.filter(u => u.subscriptionTier === 'free').length,
    totalPosts: posts?.length || 0,
    publishedPosts: posts?.filter(p => p.status === 'published').length || 0,
    postsThisMonth: 0,
    totalLikes: 0,
    totalComments: 0,
    totalRevenue: users
      .filter(u => u.subscriptionTier !== 'free')
      .reduce((acc, u) => acc + (TIER_PRICES[u.subscriptionTier] || 0), 0),
    engagementRate: 0,
    activeUsers: 0,
    chatMessages: 0,
    tierBreakdown: { free: 0, bronze: 0, silver: 0, gold: 0 }
  };

  // Tier rank for sorting (higher = better tier)
  const TIER_RANK = { free: 0, bronze: 1, silver: 2, gold: 3 };

  const filteredUsers = (() => {
    const q = searchQuery.toLowerCase().trim();
    let result = users.filter((u) => {
      // Search match
      const matchesSearch =
        !q ||
        u.email?.toLowerCase().includes(q) ||
        u.displayName?.toLowerCase().includes(q);
      // Tier match
      const userTier = u.subscriptionTier || 'free';
      const matchesTier = tierFilter === 'all' || userTier === tierFilter;
      // Role match
      const matchesRole = roleFilter === 'all' || u.role === roleFilter;
      // Status match
      const userStatus = (u.subscriptionStatus || 'active').toLowerCase();
      const matchesStatus = statusFilter === 'all' || userStatus === statusFilter;
      return matchesSearch && matchesTier && matchesRole && matchesStatus;
    });

    // Sort
    const cmp = (a, b) => {
      switch (sortBy) {
        case 'joined_asc':
          return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        case 'name_asc':
          return (a.displayName || '').localeCompare(b.displayName || '');
        case 'name_desc':
          return (b.displayName || '').localeCompare(a.displayName || '');
        case 'tier_desc':
          return (
            (TIER_RANK[b.subscriptionTier || 'free'] || 0) -
            (TIER_RANK[a.subscriptionTier || 'free'] || 0)
          );
        case 'tier_asc':
          return (
            (TIER_RANK[a.subscriptionTier || 'free'] || 0) -
            (TIER_RANK[b.subscriptionTier || 'free'] || 0)
          );
        case 'joined_desc':
        default:
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
    };
    return [...result].sort(cmp);
  })();

  // Tier counts (across full unfiltered roster)
  const tierCounts = users.reduce(
    (acc, u) => {
      const t = u.subscriptionTier || 'free';
      acc[t] = (acc[t] || 0) + 1;
      return acc;
    },
    { free: 0, bronze: 0, silver: 0, gold: 0 }
  );

  // Exports EXACTLY what the roster filters show (tier/role/status/search)
  const exportUsersCSV = () => {
    const csvCell = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ['Email', 'Display Name', 'Role', 'Tier', 'Status', 'Join Date'];
    const rows = filteredUsers.map(u => [
      u.email,
      u.displayName,
      u.role,
      u.subscriptionTier,
      u.subscriptionStatus,
      new Date(u.createdAt).toLocaleDateString()
    ]);

    const csv = [headers, ...rows].map(row => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const tierLabel = tierFilter === 'all' ? 'all' : tierFilter;
    a.download = `roster_${tierLabel}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast({
      title: 'Roster Exported',
      description: `${filteredUsers.length} member(s) exported (${tierLabel === 'all' ? 'all tiers' : tierLabel + ' tier'})`
    });
  };

  const hasActiveFilters =
    tierFilter !== 'all' ||
    roleFilter !== 'all' ||
    statusFilter !== 'all' ||
    searchQuery.trim() !== '' ||
    sortBy !== 'joined_desc';

  const clearAllFilters = () => {
    setSearchQuery('');
    setTierFilter('all');
    setRoleFilter('all');
    setStatusFilter('all');
    setSortBy('joined_desc');
  };

  return (
    <div className="min-h-screen bg-black relative" data-testid="admin-page">
      {/* Texture overlay */}
      <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0iIzAwMCIvPjxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMSA0IDBhMiAyIDAgMSAxLTQgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]"></div>
      
      <div className="container mx-auto px-4 py-8 max-w-7xl relative z-10">
        <div className="mb-8">
          <div className="inline-block mb-4">
            <div className="bg-amber-900/20 border-2 border-amber-600 px-6 py-2 inline-flex items-center gap-3">
              <Shield className="w-6 h-6 text-amber-500" />
              <h1 className="text-3xl font-headline text-amber-500 tracking-wider" data-testid="admin-title">COMMAND CENTER</h1>
            </div>
          </div>
          <p className="text-gray-400 font-typewriter">Manage your platform, investigators, and case files</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card className="bg-gray-900/90 border-2 border-amber-900/30 hover:border-amber-700/50 transition-all">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardDescription className="text-amber-500/70 font-typewriter">Total Investigators</CardDescription>
                <Users className="w-5 h-5 text-amber-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-headline text-amber-500">
                {loadingAnalytics ? <Loader2 className="w-6 h-6 animate-spin" /> : stats.totalUsers}
              </div>
              <p className="text-xs text-gray-500 mt-1 font-typewriter">
                {stats.activeSubscribers} paid • {stats.newUsersThisWeek > 0 && <span className="text-green-500">+{stats.newUsersThisWeek} this week</span>}
              </p>
              <p className="text-xs text-gray-500 mt-1 font-typewriter" data-testid="push-enabled-stat">
                <span className="text-amber-500">{stats.pushEnabled}</span> push enabled ({stats.totalUsers ? Math.round((stats.pushEnabled / stats.totalUsers) * 100) : 0}%)
                {stats.pushEnabledThisWeek > 0 && <span className="text-green-500"> • +{stats.pushEnabledThisWeek} this week</span>}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/90 border-2 border-amber-900/30 hover:border-amber-700/50 transition-all">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardDescription className="text-amber-500/70 font-typewriter">Case Files</CardDescription>
                <FileText className="w-5 h-5 text-amber-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-headline text-amber-500">
                {loadingAnalytics ? <Loader2 className="w-6 h-6 animate-spin" /> : stats.totalPosts}
              </div>
              <p className="text-xs text-gray-500 mt-1 font-typewriter">
                {stats.publishedPosts} published • {stats.totalLikes} <Heart className="w-3 h-3 inline" /> • {stats.totalComments} <MessageSquare className="w-3 h-3 inline" />
              </p>
            </CardContent>
          </Card>

          <Card 
            className="bg-gray-900/90 border-2 border-amber-900/30 hover:border-green-700/50 transition-all cursor-pointer"
            onClick={() => navigate('/admin/payments')}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardDescription className="text-amber-500/70 font-typewriter">Monthly Revenue</CardDescription>
                <CreditCard className="w-5 h-5 text-green-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-headline text-green-500" data-testid="monthly-revenue-cad">
                {loadingAnalytics ? <Loader2 className="w-6 h-6 animate-spin" /> : `$${(stats.totalRevenueCAD ?? stats.totalRevenue).toFixed(2)}`}
              </div>
              <p className="text-xs text-gray-500 mt-1 font-typewriter" data-testid="monthly-revenue-usd-note">
                {stats.totalRevenueCAD != null
                  ? `CAD • $${stats.totalRevenue.toFixed(2)} USD from ${stats.activePaidCount} active @ ${stats.fxRate}`
                  : 'USD • Click to manage payments'}
              </p>
              {(stats.cancelingMonthly > 0 || stats.pastDueMonthly > 0) && (
                <p className="text-xs text-amber-600/70 mt-0.5 font-typewriter" data-testid="monthly-revenue-at-risk-note">
                  {stats.cancelingMonthly > 0 && `$${stats.cancelingMonthly.toFixed(2)} canceling`}
                  {stats.cancelingMonthly > 0 && stats.pastDueMonthly > 0 && ' • '}
                  {stats.pastDueMonthly > 0 && `$${stats.pastDueMonthly.toFixed(2)} past due`}
                  {' (USD)'}
                </p>
              )}
            </CardContent>
          </Card>

          <Card
            className="bg-gray-900/90 border-2 border-amber-900/30 hover:border-green-700/50 transition-all cursor-pointer"
            onClick={() => navigate('/admin/payments')}
            data-testid="store-sales-card"
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardDescription className="text-amber-500/70 font-typewriter">Store Sales</CardDescription>
                <ShoppingBag className="w-5 h-5 text-green-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-headline text-green-500" data-testid="store-sales-total">
                {loadingAnalytics ? <Loader2 className="w-6 h-6 animate-spin" /> : `$${(stats.storeTotalCAD ?? stats.storeTotal ?? 0).toFixed(2)}`}
              </div>
              <p className="text-xs text-gray-500 mt-1 font-typewriter" data-testid="store-sales-note">
                {stats.storeTotalCAD != null
                  ? `CAD • $${(stats.storeTotal ?? 0).toFixed(2)} USD • ${stats.storeCount ?? 0} items all time`
                  : 'USD • all time'}
              </p>
              <p className="text-xs text-amber-600/70 mt-0.5 font-typewriter" data-testid="store-sales-recent">
                Last 30 days: ${(stats.storeLast30 ?? 0).toFixed(2)} USD • {stats.storeLast30Count ?? 0} sold
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/90 border-2 border-amber-900/30 hover:border-amber-700/50 transition-all">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardDescription className="text-amber-500/70 font-typewriter">Engagement</CardDescription>
                <Activity className="w-5 h-5 text-amber-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-headline text-amber-500">
                {loadingAnalytics ? <Loader2 className="w-6 h-6 animate-spin" /> : `${stats.engagementRate}%`}
              </div>
              <p className="text-xs text-gray-500 mt-1 font-typewriter">
                {stats.activeUsers} active this week • {stats.chatMessages} chat msgs
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tier Breakdown & Recent Activity Row */}
        {analytics && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Tier Breakdown */}
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-amber-500 tracking-wider text-lg">CLEARANCE DISTRIBUTION</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats.tierBreakdown).map(([tier, count]) => {
                    const percentage = stats.totalUsers > 0 ? Math.round((count / stats.totalUsers) * 100) : 0;
                    return (
                      <div key={tier} className="flex items-center gap-3">
                        <div className="w-16 font-headline text-sm uppercase" style={{ color: TIER_COLORS[tier] }}>
                          {tier}
                        </div>
                        <div className="flex-1 bg-gray-800 rounded-full h-4 overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all duration-500"
                            style={{ 
                              width: `${percentage}%`, 
                              backgroundColor: TIER_COLORS[tier] 
                            }}
                          />
                        </div>
                        <div className="w-16 text-right font-typewriter text-gray-400 text-sm">
                          {count} ({percentage}%)
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Recent Signups */}
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-amber-500 tracking-wider text-lg">RECENT RECRUITS</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics.recentSignups && analytics.recentSignups.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.recentSignups.map((user, idx) => (
                      <div key={user.email || `signup-${idx}`} className="flex items-center gap-3 p-2 bg-gray-800/50 rounded-lg">
                        <Avatar className="h-8 w-8 border" style={{ borderColor: TIER_COLORS[user.tier] }}>
                          <AvatarFallback className="bg-gray-700 text-amber-500 font-headline text-xs">
                            {user.displayName?.charAt(0) || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-typewriter text-gray-300 text-sm truncate">{user.displayName}</p>
                          <p className="font-typewriter text-gray-500 text-xs truncate">{user.email}</p>
                        </div>
                        <Badge 
                          className="font-headline text-xs"
                          style={{ backgroundColor: TIER_COLORS[user.tier], color: user.tier === 'gold' ? '#000' : '#fff' }}
                        >
                          {user.tier}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 font-typewriter text-center py-4">No recent signups</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-gray-900/80 border border-amber-900/30">
            <TabsTrigger value="users" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500">
              Investigators
            </TabsTrigger>
            <TabsTrigger value="content" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500">
              Case Files
            </TabsTrigger>
            <TabsTrigger value="collections" onClick={() => navigate('/admin/collections')} className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500">
              <FolderOpen className="w-4 h-4 mr-2" />
              Collections
            </TabsTrigger>
            <TabsTrigger value="tiers" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500">
              Clearance Levels
            </TabsTrigger>
            <TabsTrigger value="ranks" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500" data-testid="admin-ranks-tab">
              <Award className="w-4 h-4 mr-2" />
              Ranks &amp; Rewards
            </TabsTrigger>
            <TabsTrigger value="chat" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500">
              <MessageSquare className="w-4 h-4 mr-2" />
              Chat Rooms
            </TabsTrigger>
            <TabsTrigger value="orders" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500">
              <Camera className="w-4 h-4 mr-2" />
              Custom Orders
            </TabsTrigger>
            <TabsTrigger value="settings" onClick={() => navigate('/admin/site-settings')} className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500">
              <Settings className="w-4 h-4 mr-2" />
              Site Settings
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <EmailBlastPanel />
            <StripeAuditPanel />
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-headline text-xl text-amber-500 tracking-wider">INVESTIGATOR ROSTER</CardTitle>
                    <CardDescription className="font-typewriter text-gray-400">Manage all platform members and their clearances</CardDescription>
                  </div>
                  <Button onClick={exportUsersCSV} disabled={loadingUsers || filteredUsers.length === 0} className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider" data-testid="export-roster-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Export {tierFilter === 'all' ? 'Roster' : `${tierFilter.charAt(0).toUpperCase() + tierFilter.slice(1)}`} ({filteredUsers.length})
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Tier breakdown chips */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="text-xs font-headline tracking-widest text-amber-600/80">ROSTER:</span>
                  {(['free', 'bronze', 'silver', 'gold']).map((t) => {
                    const isActive = tierFilter === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setTierFilter(isActive ? 'all' : t)}
                        className={`px-2.5 py-1 rounded text-xs font-headline tracking-wider border transition-all ${
                          isActive
                            ? 'ring-2 ring-amber-500 scale-105'
                            : 'hover:scale-105'
                        }`}
                        style={{
                          backgroundColor: `${TIER_COLORS[t]}20`,
                          color: TIER_COLORS[t],
                          borderColor: TIER_COLORS[t],
                        }}
                        data-testid={`tier-chip-${t}`}
                      >
                        {t.toUpperCase()} · {tierCounts[t] || 0}
                      </button>
                    );
                  })}
                  <span className="text-xs font-typewriter text-gray-500 ml-auto">
                    Showing <span className="text-amber-400 font-bold">{filteredUsers.length}</span> of {users.length}
                  </span>
                </div>

                {/* Filter row */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4">
                  <div className="md:col-span-4">
                    <Input
                      placeholder="Search by name or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-gray-900/80 border-amber-900/50 focus:border-amber-600 font-typewriter"
                      data-testid="user-search-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Select value={tierFilter} onValueChange={setTierFilter}>
                      <SelectTrigger className="bg-gray-900/80 border-amber-900/50 font-typewriter" data-testid="tier-filter">
                        <SelectValue placeholder="Tier" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-amber-900/50">
                        <SelectItem value="all">All Tiers</SelectItem>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="bronze">Bronze</SelectItem>
                        <SelectItem value="silver">Silver</SelectItem>
                        <SelectItem value="gold">Gold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Select value={roleFilter} onValueChange={setRoleFilter}>
                      <SelectTrigger className="bg-gray-900/80 border-amber-900/50 font-typewriter" data-testid="role-filter">
                        <SelectValue placeholder="Role" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-amber-900/50">
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="moderator">Moderator</SelectItem>
                        <SelectItem value="member">Member</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="bg-gray-900/80 border-amber-900/50 font-typewriter" data-testid="status-filter">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-amber-900/50">
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="canceling">Canceling</SelectItem>
                        <SelectItem value="canceled">Canceled</SelectItem>
                        <SelectItem value="expired">Expired</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="bg-gray-900/80 border-amber-900/50 font-typewriter" data-testid="sort-by">
                        <SelectValue placeholder="Sort" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-amber-900/50">
                        <SelectItem value="joined_desc">Newest first</SelectItem>
                        <SelectItem value="joined_asc">Oldest first</SelectItem>
                        <SelectItem value="name_asc">Name A → Z</SelectItem>
                        <SelectItem value="name_desc">Name Z → A</SelectItem>
                        <SelectItem value="tier_desc">Highest tier first</SelectItem>
                        <SelectItem value="tier_asc">Lowest tier first</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {hasActiveFilters && (
                  <div className="mb-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAllFilters}
                      className="text-amber-500 hover:text-amber-400 hover:bg-amber-900/20 font-headline tracking-wider text-xs h-7"
                      data-testid="clear-filters-btn"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Clear filters
                    </Button>
                  </div>
                )}

                {loadingUsers ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto" />
                    <p className="mt-2 text-gray-500 font-typewriter">Loading investigators...</p>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-amber-900/50 mx-auto mb-2" />
                    <p className="text-gray-500 font-typewriter">No investigators found</p>
                  </div>
                ) : (
                <div className="rounded-md border border-amber-900/30 overflow-hidden">
                  <Table>
                    <TableHeader className="bg-amber-900/20">
                      <TableRow className="border-amber-900/30">
                        <TableHead className="font-headline text-amber-500 tracking-wide">Investigator</TableHead>
                        <TableHead className="font-headline text-amber-500 tracking-wide">Role</TableHead>
                        <TableHead className="font-headline text-amber-500 tracking-wide">Clearance</TableHead>
                        <TableHead className="font-headline text-amber-500 tracking-wide">Status</TableHead>
                        <TableHead className="font-headline text-amber-500 tracking-wide">Joined</TableHead>
                        <TableHead className="text-right font-headline text-amber-500 tracking-wide">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map(user => {
                        const tierColor = TIER_COLORS[user.subscriptionTier] || TIER_COLORS.free;
                        const tierName = user.subscriptionTier?.charAt(0).toUpperCase() + user.subscriptionTier?.slice(1) || 'Free';
                        return (
                          <TableRow key={user.id} className="border-amber-900/30 hover:bg-amber-900/10">
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <Avatar className="h-8 w-8 border-2 border-amber-700/50">
                                  <AvatarImage src={user.avatarUrl} />
                                  <AvatarFallback className="bg-amber-900/50 text-amber-500">{user.displayName?.charAt(0) || '?'}</AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium text-sm text-gray-200">{user.displayName}</p>
                                  <p className="text-xs text-gray-500 font-typewriter">{user.email}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {user.role === 'owner' && <Badge className="bg-red-700 text-white font-headline tracking-wider">OWNER</Badge>}
                              {user.role === 'moderator' && <Badge className="bg-amber-600 text-black font-headline tracking-wider">MODERATOR</Badge>}
                              {user.role === 'member' && !user.reviewerMode && <Badge variant="outline" className="border-gray-600 font-headline tracking-wider">MEMBER</Badge>}
                              {user.reviewerMode && (
                                <Badge className="bg-sky-900/70 text-sky-200 border border-sky-600/50 font-headline tracking-wider" data-testid={`user-reviewer-badge-${user.id}`}>
                                  <Eye className="w-3 h-3 mr-1" /> REVIEWER
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge style={{ backgroundColor: `${tierColor}30`, color: tierColor, borderColor: tierColor }} className="border font-headline tracking-wider">
                                {tierName}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {user.banned ? (
                                <Badge className="bg-red-800 text-white font-headline tracking-wider" data-testid={`user-banned-badge-${user.id}`}>
                                  {user.banType === 'permanent' ? 'PERMA-BANNED' : 'SUSPENDED'}
                                </Badge>
                              ) : (
                                <Badge variant={user.subscriptionStatus === 'active' ? 'default' : 'secondary'} className="font-headline tracking-wider">
                                  {(user.subscriptionStatus || 'active').toUpperCase()}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-gray-400 font-typewriter">
                              {new Date(user.createdAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-amber-500 hover:text-amber-400 hover:bg-amber-900/20 font-headline tracking-wider"
                                  onClick={() => handleEditUser(user)}
                                  data-testid={`edit-user-${user.id}`}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                                {user.role !== 'owner' && user.id !== currentUser?.id && (
                                  user.banned ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-green-500 hover:text-green-400 hover:bg-green-900/20 font-headline tracking-wider"
                                      onClick={() => handleUnbanUser(user)}
                                      data-testid={`unban-user-${user.id}`}
                                    >
                                      <ShieldCheck className="w-4 h-4 mr-1" />
                                      Unban
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-500 hover:text-red-400 hover:bg-red-900/20 font-headline tracking-wider"
                                      onClick={() => openBanDialog(user)}
                                      data-testid={`ban-user-${user.id}`}
                                    >
                                      <Ban className="w-4 h-4 mr-1" />
                                      Ban
                                    </Button>
                                  )
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content" className="space-y-4">
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-headline text-xl text-amber-500 tracking-wider">CASE FILE MANAGEMENT</CardTitle>
                    <CardDescription className="font-typewriter text-gray-400">Manage all evidence and case files</CardDescription>
                  </div>
                  <Button 
                    onClick={() => navigate('/admin/posts/new')}
                    className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider"
                    data-testid="create-case-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Case
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingPosts ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div>
                    <p className="mt-2 text-gray-500 font-typewriter">Loading case files...</p>
                  </div>
                ) : posts.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 mx-auto text-amber-900/50 mb-4" />
                    <p className="text-amber-500/70 text-lg font-typewriter mb-4">No case files yet</p>
                    <Button 
                      onClick={() => navigate('/admin/posts/new')}
                      className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Case
                    </Button>
                  </div>
                ) : (
                  <div className="rounded-md border border-amber-900/30 overflow-hidden">
                    <Table>
                      <TableHeader className="bg-amber-900/20">
                        <TableRow className="border-amber-900/30">
                          <TableHead className="font-headline text-amber-500 tracking-wide">Case Title</TableHead>
                          <TableHead className="font-headline text-amber-500 tracking-wide">Clearance</TableHead>
                          <TableHead className="font-headline text-amber-500 tracking-wide">Status</TableHead>
                          <TableHead className="font-headline text-amber-500 tracking-wide">Featured</TableHead>
                          <TableHead className="font-headline text-amber-500 tracking-wide">Published</TableHead>
                          <TableHead className="text-right font-headline text-amber-500 tracking-wide">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {posts.map(post => {
                          const tierNames = ['Free', 'Bronze', 'Silver', 'Gold'];
                          const tierColors = ['#6b7280', '#b45309', '#9ca3af', '#eab308'];
                          const tierLevel = post.minimumTierLevel || 0;
                          return (
                            <TableRow key={post.id} className="border-amber-900/30 hover:bg-amber-900/10">
                              <TableCell>
                                <div>
                                  <p className="font-medium text-gray-200">{post.title}</p>
                                  <p className="text-xs text-gray-500 font-typewriter line-clamp-1">{post.content?.substring(0, 60)}...</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge style={{ backgroundColor: `${tierColors[tierLevel]}30`, color: tierColors[tierLevel], borderColor: tierColors[tierLevel] }} className="border font-headline tracking-wider">
                                  {tierNames[tierLevel]}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant={post.status === 'published' ? 'default' : 'secondary'} className={`font-headline tracking-wider ${post.status === 'published' ? 'bg-green-700' : post.status === 'scheduled' ? 'bg-blue-700' : 'bg-gray-700'}`}>
                                  {(post.status || 'draft').toUpperCase()}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {post.featured && (
                                  <Badge className="bg-yellow-600 text-black font-headline tracking-wider">FEATURED</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-sm text-gray-400 font-typewriter">
                                {post.publishedAt ? new Date(post.publishedAt).toLocaleDateString() : '-'}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => navigate(`/post/${post.id}`)} 
                                    className="text-gray-400 hover:text-gray-300 hover:bg-gray-800"
                                  >
                                    <FileText className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => navigate(`/admin/posts/${post.id}/edit`)} 
                                    className="text-amber-500 hover:text-amber-400 hover:bg-amber-900/20"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleDeletePost(post.id)} 
                                    className="text-red-500 hover:text-red-400 hover:bg-red-900/20"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tiers Tab */}
          <TabsContent value="tiers" className="space-y-4">
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-headline text-xl text-amber-500 tracking-wider">CLEARANCE LEVELS</CardTitle>
                    <CardDescription className="font-typewriter text-gray-400">Manage pricing and access benefits</CardDescription>
                  </div>
                  <Button 
                    onClick={() => navigate('/admin/payments')}
                    className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Manage Tiers
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(TIER_COLORS).map(([tierId, color]) => {
                    const tierName = tierId.charAt(0).toUpperCase() + tierId.slice(1);
                    const tierPrices = { free: 0, bronze: 9.99, silver: 19.99, gold: 49.99 };
                    return (
                      <Card key={tierId} className="bg-gray-800/50 border-2 border-amber-900/30 hover:border-amber-700/50 transition-all">
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Crown className="w-5 h-5" style={{ color }} />
                              <CardTitle className="text-lg font-headline text-gray-100 tracking-wide">{tierName}</CardTitle>
                            </div>
                            <Badge style={{ backgroundColor: color, color: tierId === 'silver' ? '#000' : '#fff' }} className="font-headline tracking-wider">
                              {tierId === 'free' ? 'FREE' : `$${tierPrices[tierId]}/mo`}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-gray-400 font-typewriter mb-4">
                            {tierId === 'free' && 'Basic access to public content'}
                            {tierId === 'bronze' && 'Access to exclusive bronze-tier content'}
                            {tierId === 'silver' && 'Premium content and community perks'}
                            {tierId === 'gold' && 'Ultimate access with all features'}
                          </p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full border-amber-900 text-amber-500 hover:bg-amber-900/20 font-headline tracking-wider"
                            onClick={() => navigate('/admin/payments')}
                          >
                            Edit in Payment Admin
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                <p className="text-sm text-gray-500 font-typewriter mt-4 text-center">
                  For full tier management, go to Payment Admin → Tier Pricing
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Chat Rooms Tab */}
          <TabsContent value="ranks" className="space-y-4">
            <GamificationAdminPanel />
          </TabsContent>

          <TabsContent value="chat" className="space-y-4">
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-headline text-xl text-amber-500 tracking-wider">CHAT GROUP CHANNELS</CardTitle>
                    <CardDescription className="font-typewriter text-gray-400">
                      Manage chat rooms and tier restrictions
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={handleCreateRoom}
                    className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider"
                    data-testid="create-room-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    NEW CHANNEL
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingRooms ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                  </div>
                ) : chatRooms.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 font-typewriter">
                    <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No chat rooms yet</p>
                    <p className="text-sm">Create your first channel to get started</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {chatRooms.map((room) => (
                      <div 
                        key={room.id} 
                        className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-amber-900/30 hover:border-amber-700/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-amber-900/30 rounded-lg">
                            <Hash className="w-5 h-5 text-amber-500" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-headline text-gray-100 tracking-wide">{room.name}</span>
                              {room.minTier !== 'free' && (
                                <Badge 
                                  className="text-xs"
                                  style={{ backgroundColor: TIER_COLORS[room.minTier], color: room.minTier === 'silver' ? '#000' : '#fff' }}
                                >
                                  <Lock className="w-3 h-3 mr-1" />
                                  {room.minTier.charAt(0).toUpperCase() + room.minTier.slice(1)}+
                                </Badge>
                              )}
                            </div>
                            {room.description && (
                              <p className="text-sm text-gray-500 font-typewriter">{room.description}</p>
                            )}
                            <p className="text-xs text-gray-600 mt-1">
                              {room.messageCount || 0} messages
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEditRoom(room)}
                            className="text-amber-500 hover:text-amber-400 hover:bg-amber-900/20"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteRoom(room.id)}
                            className="text-red-500 hover:text-red-400 hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Custom Orders Tab */}
          <TabsContent value="orders" className="space-y-4" data-testid="admin-custom-orders-tab">
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-xl text-amber-500 tracking-wider flex items-center gap-2">
                  <Camera className="w-5 h-5" />
                  CUSTOM ORDERS
                </CardTitle>
                <CardDescription className="font-typewriter text-gray-400">
                  Custom content requests from members
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingOrders ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>
                ) : customOrders.length === 0 ? (
                  <p className="text-center text-gray-500 font-typewriter py-8">No custom orders yet</p>
                ) : (
                  <div className="space-y-4">
                    {customOrders.map(order => (
                      <div key={order.id} className="bg-gray-800/60 border border-amber-900/20 rounded-lg p-4 space-y-3" data-testid={`custom-order-${order.id}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-headline text-amber-400 tracking-wider text-sm">{order.content_type}</span>
                              <span className="text-gray-600">|</span>
                              <span className="font-typewriter text-gray-400 text-xs">{order.user_name} ({order.user_email})</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className="font-typewriter text-gray-500 text-xs">{new Date(order.created_at).toLocaleDateString()}</span>
                              <span className="text-gray-600">|</span>
                              <span className="font-typewriter text-amber-500 text-xs">{order.budget_range}</span>
                              <span className="text-gray-600">|</span>
                              <span className="font-typewriter text-gray-400 text-xs">Models: {order.models?.join(', ')}</span>
                            </div>
                          </div>
                          <Select value={order.status} onValueChange={(v) => handleOrderStatus(order.id, v)}>
                            <SelectTrigger className={`w-[130px] h-8 text-xs font-headline tracking-wider ${
                              order.status === 'pending' ? 'bg-yellow-900/30 border-yellow-700/50 text-yellow-400' :
                              order.status === 'accepted' ? 'bg-green-900/30 border-green-700/50 text-green-400' :
                              order.status === 'completed' ? 'bg-amber-900/30 border-amber-700/50 text-amber-400' :
                              'bg-red-900/30 border-red-700/50 text-red-400'
                            }`} data-testid={`order-status-${order.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-800 border-amber-900/30">
                              <SelectItem value="pending" className="text-yellow-400 font-typewriter text-xs">Pending</SelectItem>
                              <SelectItem value="accepted" className="text-green-400 font-typewriter text-xs">Accepted</SelectItem>
                              <SelectItem value="completed" className="text-amber-400 font-typewriter text-xs">Completed</SelectItem>
                              <SelectItem value="declined" className="text-red-400 font-typewriter text-xs">Declined</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <div>
                            <span className="font-headline text-xs text-amber-600 tracking-wider">DESCRIPTION</span>
                            <p className="font-typewriter text-gray-300 text-sm mt-0.5">{order.description}</p>
                          </div>
                          {order.must_haves && (
                            <div>
                              <span className="font-headline text-xs text-amber-600 tracking-wider">MUST HAVES</span>
                              <p className="font-typewriter text-gray-300 text-sm mt-0.5">{order.must_haves}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Chat Room Dialog */}
      <Dialog open={roomDialogOpen} onOpenChange={setRoomDialogOpen}>
        <DialogContent className="bg-gray-900 border-2 border-amber-900/30 sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="font-headline text-xl text-amber-500 tracking-wider">
              {editingRoom ? 'EDIT CHANNEL' : 'NEW CHANNEL'}
            </DialogTitle>
            <DialogDescription className="font-typewriter text-gray-400">
              {editingRoom ? 'Update chat room settings' : 'Create a new chat room for your community'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-amber-500 font-headline tracking-wider">CHANNEL NAME</Label>
              <Input
                value={roomForm.name}
                onChange={(e) => setRoomForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., General, VIP Lounge, Case Discussion"
                className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                data-testid="room-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-amber-500 font-headline tracking-wider">DESCRIPTION</Label>
              <Input
                value={roomForm.description}
                onChange={(e) => setRoomForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What's this channel about?"
                className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                data-testid="room-description-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-amber-500 font-headline tracking-wider">MINIMUM TIER</Label>
              <Select 
                value={roomForm.minTier} 
                onValueChange={(value) => setRoomForm(prev => ({ ...prev, minTier: value }))}
              >
                <SelectTrigger className="bg-gray-800 border-amber-900/50 focus:border-amber-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-amber-900/50">
                  <SelectItem value="free">Free (Everyone)</SelectItem>
                  <SelectItem value="bronze">Bronze+</SelectItem>
                  <SelectItem value="silver">Silver+</SelectItem>
                  <SelectItem value="gold">Gold Only</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 font-typewriter">
                Only users with this tier or higher can access this channel
              </p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="room-allow-images-toggle">
              <input type="checkbox" checked={roomForm.allowImages}
                onChange={(e) => setRoomForm(prev => ({ ...prev, allowImages: e.target.checked }))}
                className="mt-1 h-4 w-4 accent-amber-600" />
              <span>
                <span className="text-amber-500 font-headline tracking-wider text-sm block">ALLOW PHOTOS</span>
                <span className="text-xs text-gray-500 font-typewriter">Members can attach images (auto-shrunk to ≤1280px, stored on R2). Off for General Chat.</span>
              </span>
            </label>
          </div>
          
          <DialogFooter>
            <Button 
              variant="ghost" 
              onClick={() => setRoomDialogOpen(false)}
              className="font-headline tracking-wider"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveRoom}
              disabled={savingRoom || !roomForm.name.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider"
              data-testid="save-room-btn"
            >
              {savingRoom ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editingRoom ? 'UPDATE' : 'CREATE'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-gray-900 border-2 border-amber-900/30 sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="font-headline text-xl text-amber-500 tracking-wider">EDIT INVESTIGATOR</DialogTitle>
            <DialogDescription className="font-typewriter text-gray-400">
              Update role and clearance level for {editingUser?.displayName}
            </DialogDescription>
          </DialogHeader>
          
          {editingUser && (
            <div className="space-y-4 py-4">
              {/* User Info Display */}
              <div className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg border border-amber-900/30">
                <Avatar className="h-12 w-12 border-2 border-amber-700/50">
                  <AvatarImage src={editingUser.avatarUrl} />
                  <AvatarFallback className="bg-amber-900/50 text-amber-500">
                    {editingUser.displayName?.charAt(0) || '?'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-headline text-gray-100 tracking-wide">{editingUser.displayName}</p>
                  <p className="text-sm text-gray-500 font-typewriter">{editingUser.email}</p>
                </div>
              </div>

              {/* Display Name */}
              <div className="space-y-2">
                <Label className="text-amber-500 font-headline tracking-wider">DISPLAY NAME</Label>
                <Input
                  value={userEdits.displayName}
                  onChange={(e) => setUserEdits(prev => ({ ...prev, displayName: e.target.value }))}
                  className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                  data-testid="edit-user-displayname"
                />
              </div>

              {/* Role Select */}
              <div className="space-y-2">
                <Label className="text-amber-500 font-headline tracking-wider">ROLE</Label>
                <Select 
                  value={userEdits.role} 
                  onValueChange={(value) => setUserEdits(prev => ({ ...prev, role: value }))}
                  disabled={editingUser.id === currentUser?.id}
                >
                  <SelectTrigger className="bg-gray-800 border-amber-900/50 font-typewriter" data-testid="edit-user-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-amber-900/30">
                    <SelectItem value="member" className="font-typewriter">Member</SelectItem>
                    <SelectItem value="moderator" className="font-typewriter">Moderator</SelectItem>
                    <SelectItem value="owner" className="font-typewriter">Owner</SelectItem>
                  </SelectContent>
                </Select>
                {editingUser.id === currentUser?.id && (
                  <p className="text-xs text-gray-500 font-typewriter">You cannot change your own role</p>
                )}
              </div>

              {/* Tier Select */}
              <div className="space-y-2">
                <Label className="text-amber-500 font-headline tracking-wider">SUBSCRIPTION TIER</Label>
                <Select 
                  value={userEdits.subscriptionTier} 
                  onValueChange={(value) => setUserEdits(prev => ({ ...prev, subscriptionTier: value }))}
                >
                  <SelectTrigger className="bg-gray-800 border-amber-900/50 font-typewriter" data-testid="edit-user-tier">
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-amber-900/30">
                    <SelectItem value="free" className="font-typewriter">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: TIER_COLORS.free }}></div>
                        Free
                      </div>
                    </SelectItem>
                    <SelectItem value="bronze" className="font-typewriter">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: TIER_COLORS.bronze }}></div>
                        Bronze
                      </div>
                    </SelectItem>
                    <SelectItem value="silver" className="font-typewriter">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: TIER_COLORS.silver }}></div>
                        Silver
                      </div>
                    </SelectItem>
                    <SelectItem value="gold" className="font-typewriter">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: TIER_COLORS.gold }}></div>
                        Gold
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 font-typewriter">
                  This manually sets the user's tier. Does not affect Stripe subscription.
                </p>
              </div>

              {/* Moderator: Gold members may DM */}
              {userEdits.role === 'moderator' && (
                <div className="space-y-2 pt-3 border-t border-amber-900/30">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={userEdits.dmContact}
                      onChange={(e) => setUserEdits(prev => ({ ...prev, dmContact: e.target.checked }))}
                      className="h-4 w-4 accent-amber-600"
                      data-testid="edit-user-dm-contact-toggle"
                    />
                    <span className="text-amber-500 font-headline tracking-wider flex items-center gap-2"><MessageSquare className="w-4 h-4" /> COMMISSIONERS CAN DM</span>
                  </label>
                  <p className="text-xs text-gray-500 font-typewriter">
                    Gold-tier members get a "Message {userEdits.displayName || 'moderator'}" button on their Messages page. Only one moderator should have this on.
                  </p>
                </div>
              )}

              {/* Review access (preview-only account) */}
              <div className="space-y-2 pt-3 border-t border-amber-900/30">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={userEdits.reviewerMode}
                    onChange={(e) => setUserEdits(prev => ({ ...prev, reviewerMode: e.target.checked }))}
                    className="h-4 w-4 accent-amber-600"
                    data-testid="edit-user-reviewer-toggle"
                  />
                  <span className="text-amber-500 font-headline tracking-wider flex items-center gap-2"><Eye className="w-4 h-4" /> REVIEW ACCESS</span>
                </label>
                <p className="text-xs text-gray-500 font-typewriter">
                  Preview-only login: sees cover picture, title and teaser of every post across all tiers. No videos, photo galleries, comments, chat or messages. Nothing on their screen says it's a review account.
                </p>
                {userEdits.reviewerMode && (
                  <div className="space-y-1">
                    <Label className="text-amber-500 font-headline tracking-wider text-xs">LOGIN EXPIRES ON</Label>
                    <Input
                      type="date"
                      value={userEdits.reviewerExpiresAt}
                      onChange={(e) => setUserEdits(prev => ({ ...prev, reviewerExpiresAt: e.target.value }))}
                      className="bg-gray-800 border-amber-900/50 font-typewriter"
                      data-testid="edit-user-reviewer-expiry"
                    />
                    <p className="text-xs text-gray-500 font-typewriter">Leave empty for no expiry. Untick the box to end access immediately.</p>
                  </div>
                )}
              </div>

              {/* Set password */}
              <div className="space-y-2 pt-3 border-t border-amber-900/30">
                <Label className="text-amber-500 font-headline tracking-wider">SET NEW PASSWORD</Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="bg-gray-800 border-amber-900/50 font-typewriter"
                    data-testid="edit-user-new-password"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSetUserPassword}
                    disabled={settingPassword || newUserPassword.length < 8}
                    className="border-amber-700/50 text-amber-500 hover:bg-amber-900/30 font-headline tracking-wider"
                    data-testid="edit-user-set-password-btn"
                  >
                    {settingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : 'SET'}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 font-typewriter">Applies immediately; the member is not emailed.</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setEditDialogOpen(false)}
              className="border-gray-600 text-gray-400 hover:bg-gray-800 font-headline tracking-wider"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button 
              onClick={handleSaveUser}
              disabled={savingUser}
              className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider"
              data-testid="save-user-btn"
            >
              {savingUser ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ban User Dialog */}
      <Dialog open={banDialogOpen} onOpenChange={setBanDialogOpen}>
        <DialogContent className="bg-gray-900 border-2 border-red-900/40 sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="font-headline text-xl text-red-500 tracking-wider flex items-center gap-2">
              <Ban className="w-5 h-5" /> BAN INVESTIGATOR
            </DialogTitle>
            <DialogDescription className="font-typewriter text-gray-400">
              Restrict access for {banningUser?.displayName} ({banningUser?.email})
            </DialogDescription>
          </DialogHeader>

          {banningUser && (
            <div className="space-y-4 py-2">
              {/* Ban type selection */}
              <div className="space-y-2">
                <Label className="text-red-400 font-headline tracking-wider">BAN TYPE</Label>
                <div className="grid grid-cols-1 gap-2">
                  <button
                    type="button"
                    onClick={() => setBanType('suspended')}
                    data-testid="ban-type-suspended"
                    className={`text-left p-3 rounded-lg border transition-colors ${banType === 'suspended' ? 'border-amber-600 bg-amber-900/20' : 'border-gray-700 bg-gray-800/50 hover:border-amber-900/50'}`}
                  >
                    <p className="font-headline text-amber-400 tracking-wide text-sm">SUSPEND (Reversible)</p>
                    <p className="text-xs text-gray-400 font-typewriter mt-1">
                      Blocks login and kicks out any active session. Their data stays intact. Can be reinstated anytime.
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBanType('permanent')}
                    data-testid="ban-type-permanent"
                    className={`text-left p-3 rounded-lg border transition-colors ${banType === 'permanent' ? 'border-red-600 bg-red-900/20' : 'border-gray-700 bg-gray-800/50 hover:border-red-900/50'}`}
                  >
                    <p className="font-headline text-red-400 tracking-wide text-sm">PERMANENT BAN (Blacklist)</p>
                    <p className="text-xs text-gray-400 font-typewriter mt-1">
                      Everything a suspend does, plus blacklists their email (blocks re-registration) and removes their comments &amp; chat messages.
                    </p>
                  </button>
                </div>
              </div>

              {/* Reason */}
              <div className="space-y-2">
                <Label className="text-red-400 font-headline tracking-wider">REASON (shown to the user)</Label>
                <Textarea
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  placeholder="e.g. Repeated harassment in chat"
                  className="bg-gray-800 border-red-900/40 focus:border-red-600 font-typewriter"
                  data-testid="ban-reason-input"
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBanDialogOpen(false)}
              className="border-gray-600 text-gray-400 hover:bg-gray-800 font-headline tracking-wider"
              data-testid="ban-cancel-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleBanUser}
              disabled={banSubmitting}
              className="bg-red-700 hover:bg-red-800 text-white font-headline tracking-wider"
              data-testid="ban-confirm-btn"
            >
              {banSubmitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Ban className="w-4 h-4 mr-2" />
              )}
              {banType === 'permanent' ? 'Permanently Ban' : 'Suspend User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;